import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  Stack,
  Link,
} from '@mui/material';
import { Check } from '@mui/icons-material';

// COMPONENTS
import CountrySingleView from './CountrySingleView';
import Paginator from '../../../common/Paginator';
import Config from '../../../../Config';
import Loader from '../../../common/Loader';

// NETWORKS
import useAdminCountryRequest from '../../../../requests/AdminCountryRequest';

// BREADCRUMB
import { useBreadcrumb } from './BreadcrumbContext';

const ServiceCountry = ({ customer, errorHandler, navigate }) => {
  const params = useParams();
  const id = params.id;

  const { breadcrumbs } = useBreadcrumb();

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [paginatedCountries, setPaginatedCountries] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(Config.defaultPL);
  const [loading, setLoading] = useState(false);
  const [filterLetter, setFilterLetter] = useState(
    localStorage.getItem('configCountryFilterLetter') || ''
  );
  const [letters, setLetters] = useState([]);
  const [count, setCount] = useState(0);
  const { getCountries } = useAdminCountryRequest();

  useEffect(() => {
    if (customer) {
      const fetchCountries = async () => {
        setLoading(true);
        try {
          const fetchedCountries = await getCountries(errorHandler);
          const sortedCountries = sortList(fetchedCountries);
          const sortedLetters = sortLetters(sortedCountries);

          if (id) {
            const country = sortedCountries.find(
              (country) => country.country_code_id === parseInt(id, 10)
            );
            setSelectedCountry(country);
          }

          setCountries(sortedCountries);
          setLetters(sortedLetters);
          setPageNumber(1);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching countries:', error);
          errorHandler(error);
          setLoading(false);
        }
      };

      fetchCountries();
    }
  }, [customer, errorHandler]);

  useEffect(() => {
    if (!params.id) {
      setSelectedCountry(null);
    }
  }, [params]);

  const sortList = (data) => {
    return data.sort((a, b) => {
      if (a.country_name.toLowerCase() < b.country_name.toLowerCase()) {
        return -1;
      }
      if (a.country_name.toLowerCase() > b.country_name.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  };

  const sortLetters = (countries) => {
    const letterSet = new Set();
    countries.forEach((country) => {
      letterSet.add(country.country_name.toUpperCase().charAt(0));
    });
    return Array.from(letterSet).sort();
  };

  const handleLetterFilter = (letter) => {
    setPageNumber(1);

    const newLetter = letter === filterLetter ? '' : letter;
    setFilterLetter(newLetter);
    localStorage.setItem('configCountryFilterLetter', newLetter);
  };

  const changePageLength = (e) => {
    if (e.target) {
      const value = parseInt(e.target.value, 10);
      setPageNumber(1);
      setPageLength(value);
    }
  };

  const setPaginatedResults = useCallback(() => {
    if (Array.isArray(countries)) {
      let newList = [];
      let paginatedCountries = [];

      if (filterLetter) {
        countries.forEach((country) => {
          if (
            filterLetter.toLowerCase() ===
            country.country_name.charAt(0).toLowerCase()
          ) {
            newList.push(country);
          }
        });
        paginatedCountries = newList.slice(
          (pageNumber - 1) * pageLength,
          (pageNumber - 1) * pageLength + pageLength
        );
        setCount(newList.length);
      } else {
        paginatedCountries = countries.slice(
          (pageNumber - 1) * pageLength,
          (pageNumber - 1) * pageLength + pageLength
        );
        setCount(countries.length);
      }
      setPaginatedCountries(paginatedCountries);
    }
  }, [pageNumber, pageLength, filterLetter, countries]);

  useEffect(() => {
    setPaginatedResults();
  }, [setPaginatedResults]);

  const changeEvent = (i) => {
    setPageNumber(i);
  };

  const showSingleCountry = (country) => {
    navigate(`/admin/service-config/country/${country.country_code_id}`);
  };

  if (selectedCountry) {
    return (
      <Stack gap={2}>
        {breadcrumbs.length > 0 && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              padding: 0.5,
            }}
          >
            {breadcrumbs.map((breadcrumb, index) => (
              <Typography key={index} fontSize={12} color="primary.main">
                {breadcrumb}
              </Typography>
            ))}
          </Stack>
        )}

        <CountrySingleView
          country={selectedCountry}
          errorHandler={errorHandler}
          navigate={navigate}
        />
      </Stack>
    );
  }

  return (
    <Stack sx={{ paddingTop: 2 }}>
      <Typography variant="h4">Countries</Typography>

      {loading ? (
        <Loader />
      ) : (
        <Box>
          {letters.map((letter) => (
            <Button
              key={letter}
              variant={'text'}
              onClick={() => handleLetterFilter(letter)}
              size="small"
              sx={{
                minWidth: 'auto',
                padding: 0.25,
                color:
                  filterLetter === letter ? 'primary.main' : 'secondary.light',
              }}
            >
              {letter}
            </Button>
          ))}

          <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
            <Table aria-label="countries table">
              <TableHead>
                <TableRow>
                  <TableCell>Country Name</TableCell>
                  <TableCell>Country Code</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell align="center">Customers</TableCell>
                  <TableCell align="center">Premium</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCountries.map((country) => (
                  <TableRow
                    key={country.country_code_id}
                    id={`country-${country.country_code_id}`}
                  >
                    <TableCell align="left">
                      <Link
                        tabIndex={0}
                        onClick={() => showSingleCountry(country)}
                      >
                        {country.country_name}
                      </Link>
                    </TableCell>
                    <TableCell align="left">{country.country_code}</TableCell>
                    <TableCell align="left">{country.language_name}</TableCell>
                    <TableCell align="center">
                      {country.country_status === 'Y' && (
                        <Check color="success" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {country.premium_status === 'Y' && (
                        <Check color="success" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paginator
            itemCount={count}
            changeEvent={changeEvent}
            pageLength={pageLength}
            pageNumber={pageNumber}
            editFunction={changePageLength}
          />
        </Box>
      )}
    </Stack>
  );
};

export default ServiceCountry;
